import { addMonths, format, isBefore, isLastDayOfMonth, parseISO, startOfMonth } from "date-fns";

/**
 * A single calendar coverage month derived from a policy's effective dates.
 * Mirrors the backend keying (US 5045 / ADR-047): coverage months are the
 * calendar months the term touches, keyed first-of-month. Months are NOT
 * inception-anchored windows and the count is not fixed at 12.
 */
export interface CoverageMonth {
  /** 1-based ordinal within the term (display only). */
  monthNo: number;
  /** First-of-month date string (YYYY-MM-DD) — the upsert/list key. */
  coverageMonth: string;
  /** Human label, e.g. "Mar 2026". */
  label: string;
  /**
   * True when the term does not cover the whole calendar month at the start or
   * end boundary (mid-month inception or mid-month termination). Proration of
   * partial months happens downstream in the calc view, not here.
   */
  isPartial: boolean;
}

/**
 * Derives every calendar coverage month a policy term touches, inclusive of
 * partial first/last months. Returns [] for missing/invalid/reversed dates.
 *
 * Both inputs are date-only strings (YYYY-MM-DD).
 */
export function deriveCoverageMonths(effectiveStart?: string, effectiveEnd?: string): CoverageMonth[] {
  if (!effectiveStart || !effectiveEnd) return [];

  const start = parseISO(effectiveStart);
  const end = parseISO(effectiveEnd);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || isBefore(end, start)) {
    return [];
  }

  const months: CoverageMonth[] = [];
  const lastMonth = startOfMonth(end);
  let cursor = startOfMonth(start);
  let index = 0;

  // Iterate while cursor <= lastMonth.
  while (!isBefore(lastMonth, cursor)) {
    const isFirst = index === 0;
    const isLast = !isBefore(cursor, lastMonth) && !isBefore(lastMonth, cursor);

    const startsMidMonth = isFirst && start.getDate() !== 1;
    const endsMidMonth = isLast && !isLastDayOfMonth(end);

    months.push({
      monthNo: index + 1,
      coverageMonth: format(cursor, "yyyy-MM-dd"),
      label: format(cursor, "MMM yyyy"),
      isPartial: startsMidMonth || endsMidMonth,
    });

    cursor = addMonths(cursor, 1);
    index += 1;
  }

  return months;
}
